import { atom } from 'jotai';
import { loadable, unwrap } from 'jotai/utils';
import { mutationAtomWithLoadable } from 'lib/atoms';
import request from 'lib/request';
import type {
  RefreshResponse,
  RefreshStatus,
  UnitWellAssignmentFilters,
  UnitWellAssignmentsResponse,
} from './types';

const ENDPOINT = '/admin/unit_well_assignments';

export const EMPTY_FILTERS: UnitWellAssignmentFilters = {
  wellUWI: '',
  developmentAreaName: '',
  currentOperator: '',
  wellStatus: '',
  assignmentType: '',
  tier: '',
  percentBelow: '',
  multipleAssignments: false,
  sortBy: 'wellUWI',
  sortDir: 'asc',
  page: 1,
  pageSize: 100,
};

// Filter, sort and page state. The page writes it; the list re-fetches on change.
export const unitWellAssignmentFiltersAtom =
  atom<UnitWellAssignmentFilters>(EMPTY_FILTERS);

// Bumped to re-read the rows once a refresh has landed (useRefreshState).
export const rowsReloadTickAtom = atom(0);

// The refresh status as last read on its own (GET refresh_status), which is
// newer than the page's while a refresh is awaited. Null until one is read.
export const refreshStatusAtom = atom<RefreshStatus | null>(null);

// A "Refresh now" the server accepted and that has not ended: the
// lastRefreshedAt and lastFailedAt the server reported when it was asked for.
// It ends when either moves on. Server values only, so no client clock is
// compared with the server's.
export interface RefreshRequest {
  fromRefreshedAt: string | null;
  fromFailedAt: string | null;
}

export const refreshRequestAtom = atom<RefreshRequest | undefined>(undefined);

// How the last awaited refresh ended, when not by landing: it failed (with the
// server's message), or no result came within the wait. Cleared by the next
// request.
export type RefreshOutcome =
  { kind: 'failed'; message: string | null } | { kind: 'no_result' } | null;

export const refreshOutcomeAtom = atom<RefreshOutcome>(null);

// The status the page acts on: the separately read one unless the page's own
// is newer (a later page read after a landed refresh).
export const currentRefreshStatusAtom = atom((get) => {
  const shown = get(latestUnitWellAssignmentsAtom)?.refresh ?? null;
  const read = get(refreshStatusAtom);
  if (!read) return shown;
  if (!shown) return read;
  return (shown.lastRefreshedAt ?? '') > (read.lastRefreshedAt ?? '')
    ? shown
    : read;
});

// GET /admin/unit_well_assignments/refresh_status.
export const fetchRefreshStatus = async (): Promise<RefreshStatus> => {
  const response = await request.get<RefreshStatus>(
    `${ENDPOINT}/refresh_status`
  );
  if ('error' in response) throw new Error(response.error);
  return response;
};

// camelCase filter key or column id -> the snake_case param the server reads,
// for the filters and the sort (which the server allowlists). One map, so a new
// filter or sortable column is named once.
const PARAM_NAMES = {
  wellUWI: 'well_uwi',
  wellName: 'well_name',
  wellStatus: 'well_status',
  currentOperator: 'current_operator',
  developmentAreaName: 'development_area_name',
  assignmentType: 'assignment_type',
  tier: 'tier',
  producingLengthFt: 'producing_length_ft',
  lengthInUnitFt: 'length_in_unit_ft',
  percentInUnit: 'percent_in_unit',
  percentBelow: 'percent_below',
  multipleAssignments: 'multiple_assignments',
} as const;

type FilterKey = keyof UnitWellAssignmentFilters & keyof typeof PARAM_NAMES;

const FILTER_KEYS: readonly FilterKey[] = [
  'wellUWI',
  'developmentAreaName',
  'currentOperator',
  'wellStatus',
  'assignmentType',
  'tier',
  'percentBelow',
  'multipleAssignments',
];

const sortParam = (sortBy: string): string =>
  PARAM_NAMES[sortBy as keyof typeof PARAM_NAMES] ?? PARAM_NAMES.wellUWI;

// The filters as query params, shared by the page fetch and the CSV export. A
// blank filter or an unticked toggle is left out.
export const filterParams = (
  filters: UnitWellAssignmentFilters
): URLSearchParams => {
  const params = new URLSearchParams();
  FILTER_KEYS.forEach((key) => {
    const value = filters[key];
    if (value === '' || value === false) return;
    params.append(PARAM_NAMES[key], String(value));
  });
  return params;
};

// The Pusher event the finished export arrives on, on user_<id>_exports.
export const EXPORT_COMPLETE_EVENT = 'unit_well_assignments_export_complete';

// POST /admin/unit_well_assignments/export with the active filters. The server
// writes the CSV in the background; the link arrives over Pusher
// (useServerExport).
export const exportUnitWellAssignmentsAtom = atom(
  null,
  async (get): Promise<{ exportId: string }> => {
    const body = Object.fromEntries(
      filterParams(get(unitWellAssignmentFiltersAtom))
    );
    const response = await request.post<
      { exportId: string },
      { error?: string }
    >(`${ENDPOINT}/export`, body, new AbortController());

    if (response.type === 'success')
      return { exportId: response.data.exportId };
    throw new Error(
      (response.type === 'error' && response.data?.error) ||
        'Failed to start the export. Please try again.'
    );
  }
);

const unitWellAssignmentsBaseAtom = atom(async (get) => {
  get(rowsReloadTickAtom);
  const filters = get(unitWellAssignmentFiltersAtom);
  const params = filterParams(filters);
  params.append('sort_by', sortParam(filters.sortBy));
  params.append('sort_dir', filters.sortDir);
  params.append('page', filters.page.toString());
  params.append('page_size', filters.pageSize.toString());

  const response = await request.get<UnitWellAssignmentsResponse>(
    `${ENDPOINT}?${params.toString()}`
  );
  if ('error' in response) throw new Error(response.error);
  return response;
});

export const unitWellAssignmentsAtom = loadable(unitWellAssignmentsBaseAtom);

// The last response, kept while the next one loads, so a filter, sort or page change shows the
// current rows under the progress bar instead of blanking the table. A computed query can take
// seconds. null until the first response lands, and after a failed fetch: unwrap rethrows a
// rejection on read, so the failure is caught here and reported through unitWellAssignmentsAtom.
const settledUnitWellAssignmentsAtom = atom(async (get) => {
  try {
    return await get(unitWellAssignmentsBaseAtom);
  } catch (_error) {
    return null;
  }
});

export const latestUnitWellAssignmentsAtom = unwrap(
  settledUnitWellAssignmentsAtom,
  (previous) => previous ?? null
);

// POST /admin/unit_well_assignments/refresh. A 409 is not a failure: it says a
// refresh is already running, which is what the user asked for. Either way the
// page then waits for the server's status to move on (useRefreshState). Any
// other refusal (too soon after the last one, say) carries the server's reason.
export const [requestRefreshAtom, requestRefreshLoadableAtom] =
  mutationAtomWithLoadable<RefreshResponse, []>(async (get, set) => {
    const accepted = (data: RefreshResponse) => {
      const status = get(currentRefreshStatusAtom);
      set(refreshRequestAtom, {
        fromRefreshedAt: status?.lastRefreshedAt ?? null,
        fromFailedAt: status?.lastFailedAt ?? null,
      });
      set(refreshOutcomeAtom, null);
      return data;
    };
    const response = await request.post<
      RefreshResponse,
      RefreshResponse & { error?: string }
    >(`${ENDPOINT}/refresh`, {}, new AbortController());

    if (response.type === 'success') return accepted(response.data);
    if (response.type === 'error' && response.data?.refreshing) {
      return accepted(response.data);
    }
    throw new Error(
      (response.type === 'error' && response.data?.error) ||
        'Failed to start the refresh. Please try again.'
    );
  });
